import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import "./index.css";

/**
 * One QueryClient for the page, with defaults chosen for a sign-in screen
 * rather than a dashboard.
 *
 * `retry: false`: the only query here is "who is signed in", and a 401 is its
 * ordinary answer. Retrying would delay the form appearing by however long the
 * backoff takes, to re-ask a question that was already answered.
 *
 * `staleTime: 0` with a refetch on focus: somebody who signs in on another tab
 * and comes back should see this page notice, rather than offering a form for a
 * session they already have.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
    mutations: {
      // A failed sign-in must not be retried automatically: every attempt
      // counts against the throttle in the API, so a silent retry spends
      // somebody's allowance on their behalf.
      retry: false,
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("#root is missing from index.html");

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
